import { useRef } from 'react';
import { 
  FileText, 
  Download, 
  Globe, 
  BarChart3, 
  Mail, 
  MessageCircle, 
  Calendar,
  Shield,
  Users,
  BookOpen,
  CheckCircle2,
  Smartphone,
  ArrowRight,
  Star,
  Zap,
  Lock,
  Eye,
  MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function FeatureDocumentation() {
  const printRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Print Button - Hidden in print */}
      <div className="print:hidden sticky top-0 z-50 bg-slate-800 border-b border-slate-700 p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <h1 className="text-white font-semibold">Feature Documentation</h1>
          <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700">
            <Download className="w-4 h-4 mr-2" />
            Download / Print PDF
          </Button>
        </div>
      </div>

      {/* Document Content */}
      <div ref={printRef} className="max-w-4xl mx-auto p-8 print:p-0 print:max-w-none">
        <div className="bg-white text-slate-900 rounded-xl print:rounded-none shadow-2xl print:shadow-none overflow-hidden">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-12 text-center">
            <h1 className="text-4xl font-bold mb-4">Cornerstone Refund</h1>
            <p className="text-xl text-emerald-100">Website Feature Documentation</p>
            <p className="text-emerald-200 mt-2">A Comprehensive Overview of Your New Digital Platform</p>
          </div>

          {/* Executive Summary */}
          <div className="p-8 border-b">
            <div className="flex items-start gap-4">
              <div className="bg-emerald-100 p-3 rounded-lg">
                <Star className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3">Executive Summary</h2>
                <p className="text-slate-600 leading-relaxed">
                  We have built a <strong>cutting-edge, enterprise-grade website</strong> for Cornerstone Refund that positions 
                  your business as a leader in the overage fund recovery industry. This platform combines modern web 
                  technologies with sophisticated marketing automation to help you attract, engage, and convert potential 
                  clients seamlessly.
                </p>
                <p className="text-slate-600 leading-relaxed mt-4">
                  Your new website features real-time analytics, automated email sequences, live chat support, 
                  content management capabilities, and a professional design that builds trust with visitors from 
                  the moment they arrive.
                </p>
              </div>
            </div>
          </div>

          {/* Custom Domain */}
          <div className="p-8 border-b bg-slate-50">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Globe className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900 mb-3">Custom Domain Configuration</h2>
                <p className="text-slate-600 mb-4">
                  Your website has been deployed to your custom domain, providing a professional web presence 
                  that reinforces your brand identity.
                </p>
                <div className="bg-white rounded-lg p-4 border">
                  <p className="text-slate-500 text-sm">What's Included:</p>
                  <ul className="mt-2 space-y-2 text-slate-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Custom domain purchased and configured
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      SSL certificate for secure HTTPS connection
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Professional email routing capabilities
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Analytics */}
          <div className="p-8 border-b">
            <div className="flex items-start gap-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900 mb-3">Advanced Analytics Dashboard</h2>
                <p className="text-slate-600 mb-4">
                  We have created a Google Analytics account on your behalf and connected it to your custom 
                  admin dashboard, giving you powerful insights into visitor behavior.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">Custom Admin Analytics</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Real-time page view tracking</li>
                      <li>• Visitor location mapping (country & city)</li>
                      <li>• Top pages performance</li>
                      <li>• Daily/weekly/monthly trends</li>
                      <li>• Contact form submissions tracking</li>
                    </ul>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-900 mb-2">Google Analytics Integration</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      <li>• Detailed visitor demographics</li>
                      <li>• Device and browser analytics</li>
                      <li>• Session duration tracking</li>
                      <li>• Traffic source analysis</li>
                      <li>• Conversion tracking ready</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="p-8 border-b bg-slate-50">
            <div className="flex items-start gap-4">
              <div className="bg-pink-100 p-3 rounded-lg">
                <Calendar className="w-6 h-6 text-pink-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900 mb-3">Social Media Management Center</h2>
                <p className="text-slate-600 mb-4">
                  Your admin dashboard includes a comprehensive social media scheduling system that allows you 
                  to plan, schedule, and manage posts directly to all your social media platforms from one 
                  central location.
                </p>
                <div className="bg-white rounded-lg p-4 border">
                  <h4 className="font-semibold text-slate-900 mb-2">Capabilities:</h4>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Schedule posts in advance for LinkedIn, Facebook, and Instagram
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Draft posts and publish when ready
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Upload images and media for social posts
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      View all scheduled and published posts in one calendar
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      API setup guide included for connecting to your social accounts
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Live Chat */}
          <div className="p-8 border-b">
            <div className="flex items-start gap-4">
              <div className="bg-teal-100 p-3 rounded-lg">
                <MessageCircle className="w-6 h-6 text-teal-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900 mb-3">Live Chat Support System</h2>
                <p className="text-slate-600 mb-4">
                  We have set up a professional live chat system using Tawk.to, allowing you to engage with 
                  website visitors in real-time and never miss an opportunity to connect with potential clients.
                </p>
                
                <div className="bg-teal-50 rounded-lg p-6 mb-4">
                  <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-teal-600" />
                    Getting Started with Live Chat
                  </h4>
                  <p className="text-slate-600 mb-4">
                    To respond to visitor chats and receive notifications, please follow these steps:
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="bg-teal-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                      <div>
                        <p className="font-medium text-slate-900">Download the Tawk.to mobile app</p>
                        <p className="text-sm text-slate-600">Available on iOS (App Store) and Android (Play Store)</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-teal-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                      <div>
                        <p className="font-medium text-slate-900">Log in with your Tawk.to account</p>
                        <p className="text-sm text-slate-600">Use the same credentials from the widget setup</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="bg-teal-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                      <div>
                        <p className="font-medium text-slate-900">Enable push notifications</p>
                        <p className="text-sm text-slate-600">Get instant alerts when someone starts a chat</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 border">
                  <h4 className="font-semibold text-slate-900 mb-2">Ways to Respond to Chats:</h4>
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-teal-500" />
                      <strong>Tawk.to Dashboard</strong> (web browser) - dashboard.tawk.to
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-teal-500" />
                      <strong>Tawk.to Mobile App</strong> (iOS/Android) - get push notifications
                    </li>
                    <li className="flex items-center gap-2">
                      <ArrowRight className="w-4 h-4 text-teal-500" />
                      <strong>Tawk.to Desktop App</strong> (Windows/Mac) - real-time alerts
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Email Sequences */}
          <div className="p-8 border-b bg-slate-50">
            <div className="flex items-start gap-4">
              <div className="bg-amber-100 p-3 rounded-lg">
                <Mail className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-slate-900 mb-3">Automated Email Marketing Sequences</h2>
                <p className="text-slate-600 mb-4">
                  We have configured an intelligent email sequence that automatically nurtures new leads from 
                  the moment they submit your contact form. This system works 24/7 to build relationships 
                  and encourage conversions.
                </p>
                
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4 border-l-4 border-emerald-500">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded">EMAIL 1</span>
                      <span className="text-slate-500 text-sm">Sent Immediately</span>
                    </div>
                    <h4 className="font-semibold text-slate-900">Welcome & Thank You</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      Thanks clients for registering and expresses how honored you are that they've chosen 
                      to potentially do business with Cornerstone Refund.
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border-l-4 border-blue-500">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-1 rounded">EMAIL 2</span>
                      <span className="text-slate-500 text-sm">Sent After 3 Days</span>
                    </div>
                    <h4 className="font-semibold text-slate-900">Free Consultation Encouragement</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      Highlights that 80% of those who schedule a free consultation initiate their first 
                      refund within 5 days, with cash in hand within weeks.
                    </p>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border-l-4 border-purple-500">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="bg-purple-100 text-purple-700 text-xs font-bold px-2 py-1 rounded">EMAIL 3</span>
                      <span className="text-slate-500 text-sm">Sent After 8 Days</span>
                    </div>
                    <h4 className="font-semibold text-slate-900">Personal Referral Request</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      Thanks them again for considering your services and asks them to tell a family member 
                      or close friend about Cornerstone Refund. Emphasizes that you survive on personal 
                      referrals, not big advertising expenses.
                    </p>
                  </div>
                </div>

                <p className="text-slate-600 mt-4 text-sm">
                  <strong>Note:</strong> All email templates can be customized through the Admin Dashboard under 
                  "Email Sequences" — including timing, subject lines, and content.
                </p>
              </div>
            </div>
          </div>

          {/* Additional Features */}
          <div className="p-8 border-b">
            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
              <Zap className="w-6 h-6 text-amber-500" />
              Additional Features
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-50 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <BookOpen className="w-5 h-5 text-emerald-600" />
                  <h4 className="font-semibold text-slate-900">Blog & Resources Section</h4>
                </div>
                <p className="text-sm text-slate-600">
                  Educational content section with articles about overage funds, the recovery process, 
                  and fee transparency. Fully manageable from the admin dashboard.
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-slate-900">Geolocation Analytics</h4>
                </div>
                <p className="text-sm text-slate-600">
                  Track where your visitors are coming from with country and city-level analytics 
                  displayed in your custom admin dashboard.
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-slate-900">Legal Pages</h4>
                </div>
                <p className="text-sm text-slate-600">
                  Professional Privacy Policy and Terms of Service pages that protect your business 
                  and build trust with potential clients.
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Lock className="w-5 h-5 text-red-600" />
                  <h4 className="font-semibold text-slate-900">Secure Admin Dashboard</h4>
                </div>
                <p className="text-sm text-slate-600">
                  Password-protected admin area accessible from the main website, with secure 
                  authentication and session management.
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Users className="w-5 h-5 text-teal-600" />
                  <h4 className="font-semibold text-slate-900">Contact Management</h4>
                </div>
                <p className="text-sm text-slate-600">
                  View and manage all contact form submissions with details including name, email, 
                  phone, state, and message — all from one convenient location.
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Eye className="w-5 h-5 text-indigo-600" />
                  <h4 className="font-semibold text-slate-900">Multi-State Service Pages</h4>
                </div>
                <p className="text-sm text-slate-600">
                  Dedicated landing pages for California, Arizona, and Georgia with targeted 
                  messaging and state-specific information for each market.
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Star className="w-5 h-5 text-yellow-600" />
                  <h4 className="font-semibold text-slate-900">Success Stories Showcase</h4>
                </div>
                <p className="text-sm text-slate-600">
                  Visual gallery featuring real client success stories with recovered amounts 
                  displayed prominently to build trust and credibility.
                </p>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-5">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-5 h-5 text-orange-600" />
                  <h4 className="font-semibold text-slate-900">Comprehensive FAQ System</h4>
                </div>
                <p className="text-sm text-slate-600">
                  Categorized FAQs including Trust & Verification questions that address common 
                  objections and build confidence with potential clients.
                </p>
              </div>
            </div>
          </div>

          {/* Access Credentials */}
          <div className="p-8 border-b bg-amber-50">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <Lock className="w-6 h-6 text-amber-600" />
              Access Credentials & Connected Services
            </h2>
            <p className="text-slate-600 mb-6">
              Below are all the login credentials and connected services for your website. 
              <strong> Keep this information secure.</strong>
            </p>
            
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-semibold text-slate-900 mb-2">Website Admin Panel</h4>
                <p className="text-sm text-slate-600">URL: Access via gear icon in header or "Admin" link in footer</p>
                <p className="text-sm text-slate-600">Email: iamtimmorrow@gmail.com</p>
                <p className="text-sm text-slate-600">Password: Thailand2026</p>
              </div>
              
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-semibold text-slate-900 mb-2">Tawk.to Live Chat</h4>
                <p className="text-sm text-slate-600">Dashboard: dashboard.tawk.to</p>
                <p className="text-sm text-slate-600">Email: Mike@cornerstonerealestate.casa</p>
                <p className="text-sm text-slate-600">Password: Thailand2026</p>
              </div>
              
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-semibold text-slate-900 mb-2">Google Workspace / Gmail</h4>
                <p className="text-sm text-slate-600">Login: mail.google.com</p>
                <p className="text-sm text-slate-600">Email: Mike@cornerstonerealestate.casa</p>
                <p className="text-sm text-slate-600">Password: Thailand2026</p>
              </div>
              
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-semibold text-slate-900 mb-2">Resend (Email Service)</h4>
                <p className="text-sm text-slate-600">Dashboard: resend.com</p>
                <p className="text-sm text-slate-600">Verified Domain: cornerstonerealestate.casa</p>
                <p className="text-sm text-slate-600">Sender Email: Mike@cornerstonerealestate.casa</p>
              </div>
              
              <div className="bg-white rounded-lg p-4 border">
                <h4 className="font-semibold text-slate-900 mb-2">Google Analytics</h4>
                <p className="text-sm text-slate-600">Dashboard: analytics.google.com</p>
                <p className="text-sm text-slate-600">Tracking ID: G-YW020SM6YC</p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="p-8 border-b">
            <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-3">
              <MapPin className="w-6 h-6 text-emerald-600" />
              Website Contact Information
            </h2>
            <div className="bg-slate-50 rounded-lg p-6">
              <p className="text-slate-700"><strong>Phone:</strong> 405-754-2183</p>
              <p className="text-slate-700"><strong>Email:</strong> Mike@cornerstonerealestate.casa</p>
              <p className="text-slate-700"><strong>Address:</strong> 2345 E. Thomas Rd Ste 100 #510, Phoenix, AZ 85016</p>
            </div>
          </div>

          {/* Technology Stack */}
          <div className="p-8 border-b bg-gradient-to-r from-slate-900 to-slate-800 text-white">
            <h2 className="text-xl font-bold mb-4">Built with Modern Technology</h2>
            <p className="text-slate-300 mb-6">
              Your website is built using the latest web technologies, ensuring fast load times, 
              excellent search engine visibility, and a smooth user experience on all devices.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="bg-white/10 rounded-lg p-4">
                <p className="font-bold">React.js</p>
                <p className="text-xs text-slate-400">Frontend Framework</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="font-bold">FastAPI</p>
                <p className="text-xs text-slate-400">Backend API</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="font-bold">MongoDB</p>
                <p className="text-xs text-slate-400">Database</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="font-bold">Tailwind CSS</p>
                <p className="text-xs text-slate-400">Styling</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-8 text-center bg-emerald-50">
            <p className="text-slate-600 mb-2">
              This documentation was prepared for <strong>Cornerstone Refund</strong>
            </p>
            <p className="text-slate-500 text-sm">
              Developed with expertise and care • {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </p>
            <div className="mt-6 pt-6 border-t border-emerald-200">
              <p className="text-emerald-700 font-semibold">
                "Integrity • Reliance • Compassion"
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:rounded-none { border-radius: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
